export type PartnerHostKind = 'canonical' | 'www-prefixed' | 'root' | 'unknown'
export type PartnerHostZone = 'fitro' | 'fitrodev' | 'localhost' | null

export type PartnerHostInfo = {
    kind: PartnerHostKind
    hostname: string | null
    port: string | null
    zone: PartnerHostZone
    slug: string | null
    canonicalHost: string | null
}

const RESERVED_SLUGS = new Set(['api', 'app', 'backoffice', 'www'])

function withPort(hostname: string, port: string | null): string {
    return port ? `${hostname}:${port}` : hostname
}

function parseHost(host?: string | null): {hostname: string; port: string | null} | null {
    const normalized = host?.split(',')[0]?.trim().toLowerCase()
    if (!normalized) return null

    try {
        const url = new URL(`http://${normalized}`)
        return {
            hostname: url.hostname.toLowerCase(),
            port: url.port || null,
        }
    } catch {
        return null
    }
}

function classifyLocalhost(hostname: string, port: string | null): PartnerHostInfo {
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
        return {kind: 'root', hostname, port, zone: 'localhost', slug: null, canonicalHost: null}
    }

    if (!hostname.endsWith('.localhost')) {
        return {kind: 'unknown', hostname, port, zone: null, slug: null, canonicalHost: null}
    }

    const labels = hostname.split('.').filter(Boolean)
    if (labels.length === 2) {
        const slug = labels[0]
        if (RESERVED_SLUGS.has(slug)) {
            return {kind: 'root', hostname, port, zone: 'localhost', slug: null, canonicalHost: null}
        }
        return {
            kind: 'canonical',
            hostname,
            port,
            zone: 'localhost',
            slug,
            canonicalHost: `${slug}.localhost`,
        }
    }

    if (labels.length === 3 && labels[0] === 'www') {
        const slug = labels[1]
        if (!slug || RESERVED_SLUGS.has(slug)) {
            return {kind: 'root', hostname, port, zone: 'localhost', slug: null, canonicalHost: null}
        }
        return {
            kind: 'www-prefixed',
            hostname,
            port,
            zone: 'localhost',
            slug,
            canonicalHost: `${slug}.localhost`,
        }
    }

    return {kind: 'unknown', hostname, port, zone: 'localhost', slug: null, canonicalHost: null}
}

function classifyDomain(
    hostname: string,
    port: string | null,
    suffix: 'fitro.pl' | 'fitrodev.pl',
    zone: Exclude<PartnerHostZone, 'localhost' | null>
): PartnerHostInfo {
    if (hostname === suffix) {
        return {kind: 'root', hostname, port, zone, slug: null, canonicalHost: null}
    }
    if (!hostname.endsWith(`.${suffix}`)) {
        return {kind: 'unknown', hostname, port, zone: null, slug: null, canonicalHost: null}
    }

    const prefix = hostname.slice(0, -1 * (`.${suffix}`.length))
    const labels = prefix.split('.').filter(Boolean)

    if (labels.length === 1) {
        const slug = labels[0]
        if (RESERVED_SLUGS.has(slug)) {
            return {kind: 'root', hostname, port, zone, slug: null, canonicalHost: null}
        }
        return {
            kind: 'canonical',
            hostname,
            port,
            zone,
            slug,
            canonicalHost: `${slug}.${suffix}`,
        }
    }

    if (labels.length === 2 && labels[0] === 'www') {
        const slug = labels[1]
        if (!slug || RESERVED_SLUGS.has(slug)) {
            return {kind: 'root', hostname, port, zone, slug: null, canonicalHost: null}
        }
        return {
            kind: 'www-prefixed',
            hostname,
            port,
            zone,
            slug,
            canonicalHost: `${slug}.${suffix}`,
        }
    }

    return {kind: 'unknown', hostname, port, zone, slug: null, canonicalHost: null}
}

export function resolvePartnerHostInfo(host?: string | null): PartnerHostInfo {
    const parsed = parseHost(host)
    if (!parsed) {
        return {kind: 'unknown', hostname: null, port: null, zone: null, slug: null, canonicalHost: null}
    }

    const {hostname, port} = parsed
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname.endsWith('.localhost')) {
        return classifyLocalhost(hostname, port)
    }
    if (hostname === 'fitro.pl' || hostname.endsWith('.fitro.pl')) {
        return classifyDomain(hostname, port, 'fitro.pl', 'fitro')
    }
    if (hostname === 'fitrodev.pl' || hostname.endsWith('.fitrodev.pl')) {
        return classifyDomain(hostname, port, 'fitrodev.pl', 'fitrodev')
    }
    return {kind: 'unknown', hostname, port, zone: null, slug: null, canonicalHost: null}
}

// The origin a studio's pages are served from: https in production and dev, http on localhost.
export function buildTenantOrigin(hostInfo: PartnerHostInfo): string | null {
    if (!hostInfo.hostname || !hostInfo.slug || !hostInfo.zone) return null
    const host = hostInfo.port ? `${hostInfo.hostname}:${hostInfo.port}` : hostInfo.hostname
    return `${hostInfo.zone === 'localhost' ? 'http' : 'https'}://${host}`
}

// Uploaded images are served by the API of the studio's environment (api.fitro.pl for *.fitro.pl); localhost has no
// public address for them.
export function buildPublicApiOrigin(hostInfo: PartnerHostInfo): string | null {
    if (hostInfo.zone === 'fitro') return 'https://api.fitro.pl'
    if (hostInfo.zone === 'fitrodev') return 'https://api.fitrodev.pl'
    return null
}

export function buildTenantHostFromHost(host: string | null | undefined, slug: string): string | null {
    const normalizedSlug = slug?.trim().toLowerCase()
    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(normalizedSlug)) return null
    if (RESERVED_SLUGS.has(normalizedSlug)) return null

    const info = resolvePartnerHostInfo(host)
    if (info.zone === 'fitro') return withPort(`${normalizedSlug}.fitro.pl`, info.port)
    if (info.zone === 'fitrodev') return withPort(`${normalizedSlug}.fitrodev.pl`, info.port)
    if (info.zone === 'localhost') return withPort(`${normalizedSlug}.localhost`, info.port)
    return null
}

export function buildTenantAbsoluteUrl(
    slug: string,
    options: {path?: string} = {}
): string | null {
    if (typeof window === 'undefined') return null

    const host = buildTenantHostFromHost(window.location.host, slug)
    if (!host) return null

    const path = options.path ?? '/calendar'
    const protocol = window.location.protocol
    return `${protocol}//${host}${path}`
}
